export type AssociationTab = string;

export type SummaryMatchApiParams = Record<string, string[]>;

export type SummaryMatchStats = {
  evidenceRowCount: number;
  uniqueAssociationCount: number;
  matchFilterCount: number;
  matchFilterLabel: string;
};

function normalizeCui(value: string | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(/^UMLS_/i, '')
    .toUpperCase();
}

function toDiseaseApiValue(cui: string): string {
  const normalized = normalizeCui(cui);
  return normalized ? `UMLS_${normalized}` : '';
}

function pickField(row: Record<string, string>, keys: string[]): string {
  for (const key of keys) {
    const value = row[key];
    if (value != null && String(value).trim() !== '') {
      return String(value).trim();
    }
  }
  return '';
}

function isDiseaseIdent(id: string): boolean {
  return /^C\d/i.test(String(id ?? '').trim());
}

export type SummaryMatchDimension = 'gene' | 'disease' | 'variant';

export function resolveSummaryMatchDimension(
  tab: AssociationTab,
  view: string,
  searchItems: Array<{ id: string }> = [],
): SummaryMatchDimension | null {
  const normalizedTab = String(tab).toUpperCase();
  const normalizedView = String(view).toUpperCase();

  if (normalizedTab === 'GDA') {
    if (normalizedView === 'GENES') return 'disease';
    if (normalizedView === 'DISEASES') return 'gene';
    const firstId = searchItems[0]?.id ?? '';
    return isDiseaseIdent(firstId) ? 'gene' : 'disease';
  }

  if (normalizedTab === 'VDA') {
    // Match by the genes of the variants found in the filtered evidence.
    return 'gene';
  }

  // DDA (disease-disease) is intentionally unsupported: no gene dimension to match.
  return null;
}

export function buildAssociationKey(
  row: Record<string, string>,
  tab: AssociationTab,
): string | null {
  const normalizedTab = String(tab).toUpperCase();
  const disease = normalizeCui(
    pickField(row, ['diseaseUMLSCUI', 'disease', 'disease_name', 'diseaseName']),
  );
  if (!disease) return null;

  if (normalizedTab === 'GDA') {
    const gene = pickField(row, ['geneSymbol', 'gene_symbol', 'symbol']).toUpperCase();
    if (!gene) return null;
    return `gda:${gene}|${disease}`;
  }

  if (normalizedTab === 'VDA') {
    const variant = pickField(row, ['variantStrID', 'variant_name', 'strID', 'strId']);
    if (!variant) return null;
    return `vda:${variant}|${disease}`;
  }

  return null;
}

export function collectAssociationKeys(
  rows: Record<string, string>[],
  tab: AssociationTab,
): Set<string> {
  const keys = new Set<string>();
  rows.forEach(row => {
    const key = buildAssociationKey(row, tab);
    if (key) keys.add(key);
  });
  return keys;
}

const MATCH_FILTER_LABELS: Record<SummaryMatchDimension, string> = {
  gene: 'genes',
  disease: 'diseases',
  variant: 'variants',
};

export function extractSummaryMatchApiParams(
  evidenceRows: Record<string, string>[],
  tab: AssociationTab,
  view: string,
  searchItems: Array<{ id: string }> = [],
): { params: SummaryMatchApiParams; dimension: SummaryMatchDimension } | null {
  const dimension = resolveSummaryMatchDimension(tab, view, searchItems);
  if (!dimension) return null;

  const values = new Set<string>();

  if (dimension === 'gene') {
    evidenceRows.forEach(row => {
      const id = pickField(row, ['gene_ncbi_id', 'geneNcbiID', 'ncbiID', 'geneID']);
      if (id) values.add(id);
    });
    if (values.size === 0) return null;
    return { params: { gene_ncbi_id_filter: [...values] }, dimension };
  }

  if (dimension === 'disease') {
    evidenceRows.forEach(row => {
      const cui = toDiseaseApiValue(pickField(row, ['diseaseUMLSCUI', 'disease', 'diseaseName']));
      if (cui) values.add(cui);
    });
    if (values.size === 0) return null;
    return { params: { disease: [...values] }, dimension };
  }

  evidenceRows.forEach(row => {
    const variant = pickField(row, ['variantStrID', 'variant_name', 'variant', 'strID', 'strId']);
    if (variant) values.add(variant);
  });
  if (values.size === 0) return null;
  return { params: { variant: [...values] }, dimension };
}

export function buildSummaryMatchStats(
  evidenceRows: Record<string, string>[],
  tab: AssociationTab,
  extracted: { params: SummaryMatchApiParams; dimension: SummaryMatchDimension },
): SummaryMatchStats {
  const associationKeys = collectAssociationKeys(evidenceRows, tab);
  const matchFilterCount = Object.values(extracted.params)[0]?.length ?? 0;

  return {
    evidenceRowCount: evidenceRows.length,
    uniqueAssociationCount: associationKeys.size,
    matchFilterCount,
    matchFilterLabel: MATCH_FILTER_LABELS[extracted.dimension],
  };
}

export function getJoinKeyUiColumns(tab: AssociationTab): string[] {
  const normalizedTab = String(tab).toUpperCase();
  if (normalizedTab === 'VDA') {
    return ['variantStrID', 'diseaseUMLSCUI', 'diseaseName'];
  }
  return ['geneSymbol', 'diseaseUMLSCUI', 'diseaseName'];
}

export function getEvidenceDownloadColumnsForMatch(
  tab: AssociationTab,
  view: string,
  searchItems: Array<{ id: string }> = [],
): string[] {
  const dimension = resolveSummaryMatchDimension(tab, view, searchItems);
  const base = getJoinKeyUiColumns(tab);

  if (dimension === 'gene') {
    return [...base, 'gene_ncbi_id', 'geneNcbiID', 'ncbiID'];
  }
  if (dimension === 'variant') {
    return [...base, 'variantStrID', 'variant', 'strID'];
  }
  return base;
}

export function withForcedDownloadColumns(
  columnVisibilityModel: Record<string, boolean> | undefined,
  forcedUiKeys: string[],
): Record<string, boolean> {
  const base = { ...(columnVisibilityModel ?? {}) };
  forcedUiKeys.forEach(key => {
    base[key] = true;
  });
  return base;
}
